import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  AlertCircle, ArrowLeft, Briefcase, Camera, Check, ChevronDown,
  Mail, MapPin, Phone, Save, Shield, Upload, User, Users, X
} from 'lucide-react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { auth, db, storage } from '../firebase/config';

const PRONOUN_OPTIONS = ['He/Him', 'She/Her', 'They/Them', 'Ze/Zir', 'Prefer not to say'];
const ROLE_OPTIONS = [
  'Administrator',
  'Manager',
  'Employee',
  'Accountant',
  'Sales Representative',
  'Support Staff'
];
const COUNTRY_OPTIONS = [
  'United States',
  'Canada',
  'United Kingdom',
  'Australia',
  'Germany',
  'France',
  'Japan',
  'India',
  'Brazil',
  'South Africa'
];

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  pronoun: '',
  email: '',
  mobile: '',
  address: '',
  country: '',
  businessId: '',
  userRole: '',
  position: ''
};

const ProfileSettings = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, refreshUserProfile } = useAuth();

  const [profileData, setProfileData] = useState(EMPTY_FORM);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [openDropdown, setOpenDropdown] = useState('');

  const fallbackEmail = currentUser?.email || '';
  const currentPhoto = userProfile?.profileImageUrl || currentUser?.photoURL || null;

  const profileHeading = useMemo(() => {
    if (profileData.firstName || profileData.lastName) {
      return `${profileData.firstName} ${profileData.lastName}`.trim();
    }
    return currentUser?.displayName || currentUser?.email || 'Profile';
  }, [profileData.firstName, profileData.lastName, currentUser]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const loadUserProfile = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
        const data = userDoc.exists() ? userDoc.data() : {};

        setProfileData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          pronoun: data.pronoun || '',
          email: data.email || currentUser.email || '',
          mobile: data.mobile || '',
          address: data.address || '',
          country: data.country || '',
          businessId: data.businessId || '',
          userRole: data.userRole || '',
          position: data.position || ''
        });
        setAvatarPreview(data.profileImageUrl || currentUser.photoURL || null);
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load profile information.');
      }
    };

    loadUserProfile();
  }, [currentUser, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleDropdownSelect = (field, value) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: '' }));
    setOpenDropdown('');
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Profile photo must be less than 5MB.');
      return;
    }

    setError('');
    setAvatarFile(file);

    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(currentPhoto);
    setUploadProgress(0);
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!profileData.firstName.trim()) nextErrors.firstName = 'First name is required';
    if (!profileData.lastName.trim()) nextErrors.lastName = 'Last name is required';
    if (!profileData.email.trim()) nextErrors.email = 'Email is required';
    if (!/\S+@\S+\.\S+/.test(profileData.email)) nextErrors.email = 'Email is invalid';
    if (!profileData.mobile.trim()) nextErrors.mobile = 'Mobile number is required';
    if (!profileData.address.trim()) nextErrors.address = 'Address is required';
    if (!profileData.country) nextErrors.country = 'Country is required';
    if (!profileData.userRole) nextErrors.userRole = 'User role is required';
    if (!profileData.position.trim()) nextErrors.position = 'Position is required';

    return nextErrors;
  };

  const uploadAvatar = async () => {
    if (!avatarFile || !currentUser) {
      return currentPhoto;
    }

    const storageRef = ref(storage, `profileImages/${currentUser.uid}/${Date.now()}_${avatarFile.name}`);
    const uploadTask = uploadBytesResumable(storageRef, avatarFile);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        },
        reject,
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!currentUser) {
      navigate('/login');
      return;
    }

    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setMessage('');

      const profileImageUrl = await uploadAvatar();
      const fullName = `${profileData.firstName} ${profileData.lastName}`.trim();

      await setDoc(
        doc(db, 'userProfiles', currentUser.uid),
        {
          firstName: profileData.firstName.trim(),
          lastName: profileData.lastName.trim(),
          fullName,
          pronoun: profileData.pronoun || null,
          email: profileData.email.trim(),
          mobile: profileData.mobile.trim(),
          address: profileData.address.trim(),
          country: profileData.country,
          businessId: profileData.businessId.trim() || null,
          userRole: profileData.userRole,
          position: profileData.position.trim(),
          profileImageUrl: profileImageUrl || null,
          uid: currentUser.uid,
          updatedAt: serverTimestamp(),
          createdAt: userProfile?.createdAt || serverTimestamp()
        },
        { merge: true }
      );

      await setDoc(
        doc(db, 'users', currentUser.uid),
        {
          displayName: fullName,
          email: profileData.email.trim(),
          profileImageUrl: profileImageUrl || null,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );

      await updateProfile(auth.currentUser, {
        displayName: fullName,
        photoURL: profileImageUrl || null
      });

      await refreshUserProfile();
      setAvatarFile(null);
      setUploadProgress(0);
      setMessage('Profile updated successfully.');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderTextInput = (label, name, icon, props = {}) => {
    const Icon = icon;
    return (
      <div>
        <label htmlFor={name} className="mb-1 block text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Icon size={16} className="text-gray-400" />
          </div>
          <input
            id={name}
            name={name}
            value={profileData[name]}
            onChange={handleChange}
            className={`w-full rounded-lg border px-4 py-2.5 pl-10 focus:border-[#190192] focus:outline-none focus:ring-2 focus:ring-[#190192] ${
              formErrors[name] ? 'border-red-300' : 'border-gray-300'
            }`}
            {...props}
          />
        </div>
        {formErrors[name] && <p className="mt-1 text-xs text-red-600">{formErrors[name]}</p>}
      </div>
    );
  };

  const renderDropdown = (label, name, icon, options, placeholder) => {
    const Icon = icon;
    return (
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Icon size={16} className="text-gray-400" />
          </div>
          <button
            type="button"
            className={`flex w-full items-center justify-between rounded-lg border py-2.5 pl-10 pr-3 text-left focus:border-[#190192] focus:outline-none focus:ring-2 focus:ring-[#190192] ${
              formErrors[name] ? 'border-red-300' : 'border-gray-300'
            }`}
            onClick={() => setOpenDropdown((prev) => (prev === name ? '' : name))}
          >
            <span className={profileData[name] ? 'text-gray-900' : 'text-gray-400'}>
              {profileData[name] || placeholder}
            </span>
            <ChevronDown size={16} className="text-gray-400" />
          </button>
          {openDropdown === name && (
            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
              {options.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                    profileData[name] === option ? 'bg-purple-50 text-[#190192]' : 'text-gray-700'
                  }`}
                  onClick={() => handleDropdownSelect(name, option)}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
        {formErrors[name] && <p className="mt-1 text-xs text-red-600">{formErrors[name]}</p>}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-3 inline-flex items-center text-sm font-medium text-[#190192] hover:text-[#2C0DB5]"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Profile Management</h1>
          <p className="text-sm text-gray-500">Manage your account details used across BizCentral.</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gradient-to-r from-[#190192] to-[#3B0DB5] px-6 py-5 text-white">
          <div className="flex items-center gap-4">
            <div className="relative">
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt={profileHeading}
                  className="h-20 w-20 rounded-full border-2 border-white/30 object-cover"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-white/30 bg-white/10">
                  <User size={30} />
                </div>
              )}
              <label className="absolute bottom-0 right-0 cursor-pointer rounded-full bg-white p-2 text-[#190192] shadow">
                <Camera size={14} />
                <input type="file" className="hidden" accept="image/*" onChange={handleAvatarChange} />
              </label>
            </div>
            <div>
              <h2 className="text-xl font-semibold">{profileHeading}</h2>
              <p className="text-sm text-purple-100">{profileData.position || profileData.userRole || 'Account user'}</p>
              {avatarFile && (
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="mt-2 inline-flex items-center text-xs text-white/90 hover:text-white"
                >
                  <X size={12} className="mr-1" />
                  Remove new photo
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-6 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div className="mb-6 flex items-start gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700">
              <Check size={16} className="mt-0.5 flex-shrink-0" />
              <span>{message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <section>
              <div className="mb-4 flex items-center gap-2">
                <User size={18} className="text-[#190192]" />
                <h3 className="text-lg font-semibold text-gray-900">Personal Details</h3>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                {renderTextInput('First Name', 'firstName', User, { type: 'text', placeholder: 'Enter your first name' })}
                {renderTextInput('Last Name', 'lastName', User, { type: 'text', placeholder: 'Enter your last name' })}
                {renderDropdown('Pronoun', 'pronoun', User, PRONOUN_OPTIONS, 'Select pronoun')}
                {renderTextInput('Email', 'email', Mail, { type: 'email', placeholder: fallbackEmail || 'Enter your email' })}
              </div>
            </section>

            <section>
              <div className="mb-4 flex items-center gap-2">
                <MapPin size={18} className="text-[#190192]" />
                <h3 className="text-lg font-semibold text-gray-900">Contact Details</h3>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                {renderTextInput('Mobile Number', 'mobile', Phone, { type: 'tel', placeholder: 'Enter your mobile number' })}
                {renderDropdown('Country', 'country', MapPin, COUNTRY_OPTIONS, 'Select your country')}
              </div>
              <div className="mt-6">
                <label htmlFor="address" className="mb-1 block text-sm font-medium text-gray-700">
                  Address
                </label>
                <textarea
                  id="address"
                  name="address"
                  rows="3"
                  value={profileData.address}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-4 py-2.5 focus:border-[#190192] focus:outline-none focus:ring-2 focus:ring-[#190192] ${
                    formErrors.address ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your address"
                />
                {formErrors.address && <p className="mt-1 text-xs text-red-600">{formErrors.address}</p>}
              </div>
            </section>

            <section>
              <div className="mb-4 flex items-center gap-2">
                <Shield size={18} className="text-[#190192]" />
                <h3 className="text-lg font-semibold text-gray-900">Work Details</h3>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                {renderTextInput('Business ID', 'businessId', Briefcase, { type: 'text', placeholder: 'Enter business ID if applicable' })}
                {renderTextInput('Position', 'position', Briefcase, { type: 'text', placeholder: 'Enter your position' })}
                {renderDropdown('User Role', 'userRole', Users, ROLE_OPTIONS, 'Select your role')}
              </div>
            </section>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div>
                <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <Upload size={12} />
                    Uploading photo
                  </span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-200">
                  <div className="h-2 rounded-full bg-[#190192]" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center rounded-lg bg-[#190192] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#2C0DB5] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Save size={16} className="mr-2" />
                {isLoading ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
